import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { MultiValue } from 'react-select';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import moment from 'moment';
import { SelectMultiCreatable } from '@/components/element/Form/SelectMultiCreatable';
import { objectToQueryString } from '@/utils';

type SearchCondition = {
  identifier: string;
  billing_year_months: string[];
  archived: boolean;
};

export const PlDataElement = () => {
  const { _w_ } = useCurrentWorkspace();
  const [searchCondition, setSearchCondition] = useState<SearchCondition>({
    identifier: _w_!.identifier,
    billing_year_months: [moment().subtract(1, 'month').format('YYYY/MM')],
    archived: true,
  });
  const [queryString, setQueryString] = useState<string>('');

  useEffect(() => {
    setQueryString((_) => objectToQueryString({ ...searchCondition }));
  }, [searchCondition]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center">
        <div>
          <h5 className="mb-0">売上・費用データ</h5>
        </div>
        <button
          className="btn btn-icon-only btn-dark mx-2"
          data-bs-toggle="modal"
          data-bs-target="#modal-pl-data-filter"
        >
          <i className="fas fa-filter"></i>
        </button>
      </div>

      <div className="d-flex px-4 mb-0 align-items-center flex-wrap">
        {searchCondition.billing_year_months.map((d, i) => (
          <span key={`ym-${i}`} className="badge rounded-pill badge-info mx-1 my-1">
            {d}
          </span>
        ))}
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <table className="table align-items-center">
            <thead>
              <tr>
                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">データ</th>
                <th
                  className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2"
                  style={{ width: 140 }}
                ></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-xs px-2 font-weight-bold">売上データ</td>
                <td className="text-xs px-2 font-weight-bold">
                  <a
                    href={`${process.env.REACT_APP_API_URL}/api/workspaces/${
                      _w_!.identifier
                    }/invoice_tasks/export_csv.csv?${queryString}`}
                    className="btn btn-icon btn-dark me-3"
                  >
                    <i className="fas fa-file-download" aria-hidden="true"></i> エクスポート
                  </a>
                </td>
              </tr>
              <tr>
                <td className="text-xs px-2 font-weight-bold">費用データ</td>
                <td className="text-xs px-2 font-weight-bold">
                  <a
                    href={`${process.env.REACT_APP_API_URL}/api/workspaces/${
                      _w_!.identifier
                    }/payment_tasks/export_csv.csv?${queryString}`}
                    className="btn btn-icon btn-dark me-3"
                  >
                    <i className="fas fa-file-download" aria-hidden="true"></i> エクスポート
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-footer pt-0 d-flex justify-content-end"></div>
      <FilterModal searchCondition={searchCondition} setSearchCondition={setSearchCondition} />
    </div>
  );
};

type FilterModalProps = {
  searchCondition: SearchCondition;
  setSearchCondition: Dispatch<SetStateAction<SearchCondition>>;
};
const FilterModal = ({ searchCondition, setSearchCondition }: FilterModalProps) => {
  const [condition, setCondition] = useState<SearchCondition>({ ...searchCondition });

  useEffect(() => {
    setCondition({ ...searchCondition });
  }, [searchCondition]);

  useEffect(() => {
    if (condition.billing_year_months.length == 0) {
      setCondition({ ...condition, billing_year_months: [moment().subtract(1, 'month').format('YYYY/MM')] });
    }
  }, [condition]);

  const onYearMonthsChange = (d: MultiValue<SelectOptionType>) => {
    setCondition((c) => ({ ...c, billing_year_months: d.map((a) => a.label) }));
  };

  const onApplyClick = () => {
    setSearchCondition((_) => ({ ...condition }));
  };

  return (
    <div
      className="modal fade"
      id="modal-pl-data-filter"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="modal-form"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-md" role="document">
        <div className="modal-content">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                フィルタ
              </h5>
              <button type="button" className="btn-close text-dark" data-bs-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <form>
                <div className="form-group">
                  <label>請求月</label>
                  <YearMonthSelect onChange={onYearMonthsChange} selectedLabels={condition.billing_year_months} />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                閉じる
              </button>
              <button type="button" className="btn bg-gradient-primary" data-bs-dismiss="modal" onClick={onApplyClick}>
                適用
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const YearMonthSelect = ({
  onChange,
  selectedLabels,
}: {
  onChange: (d: MultiValue<SelectOptionType>) => void;
  selectedLabels: string[];
}) => {
  const [options, setOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    const m = moment();
    const yearMonths: SelectOptionType[] = [...Array(24)].map((): SelectOptionType => {
      m.subtract(1, 'month');
      return { value: Number(m.format('YYYYMM')), label: m.format('YYYY/MM') };
    });
    setOptions(yearMonths);
  }, []);

  if (options == null) return <></>;

  return (
    <SelectMultiCreatable
      options={options}
      setOptions={setOptions}
      selectedLabels={selectedLabels}
      onChange={onChange}
    />
  );
};
